import type * as React from "react";

import { cn } from "../cn";

// Design spec §8.2 EmptyState (DTP `.empty`): an icon tile (52, radius 14, --secondary with a
// hairline, icon in --text-faint) over a 16 / 600 title and a 13 px muted line at most 390 wide;
// actions 8 px below. `page` pads 56 24, `card` is its own hairline card padded 32, `compact`
// pads 16 with a 40 px tile (table bodies, side panels).

type IconType = React.ComponentType<{ className?: string; "aria-hidden"?: boolean }>;

const VARIANT = {
  page: "px-6 py-14",
  card: "rounded-lg border border-border bg-card px-6 py-8",
  compact: "p-4",
} as const;

export type EmptyStateProps = Omit<React.ComponentProps<"div">, "title"> & {
  icon?: IconType;
  title: React.ReactNode;
  description?: React.ReactNode;
  actions?: React.ReactNode;
  variant?: keyof typeof VARIANT;
  align?: "center" | "start";
  /** The title's element: status pages pass `h1` (it then reads 24 / 650). */
  titleAs?: "h1" | "h2" | "h3" | "p";
};

export function EmptyState({
  icon: Icon,
  title,
  description,
  actions,
  variant = "page",
  align = "center",
  titleAs: Title = "h2",
  className,
  ...props
}: EmptyStateProps) {
  const compact = variant === "compact";
  return (
    <div
      data-slot="empty-state"
      data-variant={variant}
      className={cn(
        "flex w-full min-w-0 flex-col gap-2.5",
        align === "center" ? "items-center text-center text-balance" : "items-start text-left",
        VARIANT[variant],
        className,
      )}
      {...props}
    >
      {Icon ? (
        <span
          className={cn(
            "mb-1 flex shrink-0 items-center justify-center border border-border bg-secondary text-faint",
            compact
              ? "size-10 rounded-[11px] [&_svg]:size-[18px]"
              : "size-[52px] rounded-xl [&_svg]:size-[22px]",
          )}
        >
          <Icon aria-hidden={true} />
        </span>
      ) : null}
      <Title
        className={cn(
          "font-heading text-foreground",
          Title === "h1"
            ? "text-xl font-strong tracking-[-0.6px]"
            : compact
              ? "text-sm font-semibold"
              : "text-md font-semibold tracking-[-0.2px]",
        )}
      >
        {title}
      </Title>
      {description ? (
        <div className="max-w-[390px] text-sm leading-[1.65] text-muted-foreground">
          {description}
        </div>
      ) : null}
      {actions ? <div className="mt-2 flex flex-wrap items-center gap-2">{actions}</div> : null}
    </div>
  );
}
